import { Matrix4, Ray, Vector2, Vector4 } from 'three';
import { getOrThrow } from '../../../cross-team-shared/misc-utils/strict-record';
import { SettingState } from '../../../cross-team-shared/state/setting-state';
import {
  Point2D,
  Point3D,
} from '../../../team-apis/otterdam/answers/shape-typings';
import {
  ProjectionCamera,
  updateProjectionCamera,
} from '../../renderers/projection/projection-camera';
import { PointCloudContentState } from '../../state/point-cloud-content-state';
import { getCameraData } from '../calibration-utils';

export interface ProjectTo2DOptions {
  outputName: string;
  modelMatrix: Matrix4;
}

export abstract class AbstractProjectionUtils {
  protected projectionCamera = new ProjectionCamera();

  protected getImageSize(state: PointCloudContentState, outputName: string) {
    return new Vector2().fromArray(
      getOrThrow(state.projectionImageSize, outputName),
    );
  }

  protected updateProjectionCamera(
    state: PointCloudContentState,
    settingState: SettingState,
    outputName: string,
  ) {
    updateProjectionCamera(
      this.projectionCamera,
      getCameraData(
        state.calibrationData[outputName],
        settingState.calibrationOptions[outputName],
        state.render.frameNumber,
      ),
      getOrThrow(state.projectionImageSize, outputName),
    );
  }

  // eslint-disable-next-line complexity -- not really that complex
  protected isWithinFrustum(
    projectedInImage: Vector2,
    imageSize: Vector2,
    clipSpaceW: number,
    clipSpaceZ: number,
  ): boolean {
    return (
      clipSpaceW > 0 &&
      clipSpaceZ >= -1 &&
      projectedInImage.x <= imageSize.x &&
      projectedInImage.y <= imageSize.y &&
      projectedInImage.x >= 0 &&
      projectedInImage.y >= 0
    );
  }

  protected localToClipSpace(localPoint: Vector4, options: ProjectTo2DOptions) {
    const viewMatrix = this.projectionCamera.matrixWorldInverse;
    const projectionMatrix = this.projectionCamera.projectionMatrix;

    return localPoint
      .applyMatrix4(options.modelMatrix)
      .applyMatrix4(viewMatrix)
      .applyMatrix4(projectionMatrix);
  }

  /**
   * Projects a 3D position into 2D in screen coordinates where
   * origin coordinate (0, 0) is the bottom left corner of the screen.
   *
   * @param point 3D point in world coordinates
   *
   * @returns 2D position in screen coordinates.
   */
  abstract projectTo2D(
    point: Point3D,
    settingState: SettingState,
    state: PointCloudContentState,
    options: ProjectTo2DOptions,
  ): { position: Point2D; isInsideFrustum: boolean };

  abstract projectTo3D(
    pixel: Vector2,
    state: PointCloudContentState,
    outputName: string,
  ): Ray;
}
